import { ReactElement } from "react";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { EventPreviewModel } from "client/Api/Hub/Events/eventViewer";
import { HubItem } from "./HubItem/HubItem";
import { ActiveFilter } from "../../../common";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	ListLayout,
	ListLayoutCollapseMode
} from "../../../ListLayout/ListLayout";

export type ListProps = {
	onNavigateToItem: (id: number) => void;
	getCategory: (categoryId: number | null) => HubItemCategoryModel | undefined;
	onActiveFilterChange: (filter: ActiveFilter) => void;
	onSelectedCategoryIdChange: (categoryId: number) => void;
	items: readonly EventPreviewModel[];
	kind: "overview" | "relevant";
	shouldReserveSpaceForFloatingActionButton?: boolean;
	timezone: TimeZone;
} & (
	{ hasMoreItems?: false; }
	|
	{
		hasMoreItems: boolean;
		onLoadmore: () => void;
		isBusy: boolean;
	}
) & (
	{
		collapseMode?: undefined;
	}
	|
	{
		collapseMode: ListLayoutCollapseMode;
		renderMoreButton: ReactElement;
		shouldAlwaysRenderMoreButton?: boolean;
	}
);

export const List = (props: ListProps): ReactElement => {
	const {
		onNavigateToItem,
		getCategory,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		items,
		shouldReserveSpaceForFloatingActionButton,
		timezone,
		kind
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	// Using server side pagination it is possible that the data of item(s) changes resulting
	// that the order of the items in the set changes, which makes it that client side you could
	// have multiple items with the same id. To counter this, we suffix the number of the id duplicate.
	const idCounters = new Map<number, number>();
	const getKey = (id: number): string | number => {
		const occurrence: number = (idCounters.get(id) ?? 0) + 1;

		idCounters.set(
			id,
			occurrence
		);

		if (occurrence === 1) {
			return id;
		}

		return `${id}-${occurrence}`;
	};

	return (
		<ListLayout
			{...(
				props.collapseMode !== undefined
					? {
						collapseMode: props.collapseMode,
						renderMoreButton: props.renderMoreButton,
						shouldAlwaysRenderMoreButton: props.shouldAlwaysRenderMoreButton
					}
					: {
						collapseMode: undefined
					}
			)}
			trackingId={`event-list-${kind}`}
			hasBorderTop={isMobile}
			renderAfterItems={(
				<>
					{props.hasMoreItems && (
						<ListLayout.LoadMoreIndicator
							onRequestMoreItems={props.onLoadmore}
							hasMoreItems={props.hasMoreItems}
							isLoadingData={props.isBusy}
						/>
					)}
					{shouldReserveSpaceForFloatingActionButton && (
						<ListLayout.MobilePlusMenuSpacing />
					)}
				</>
			)}
		>
			{items.map(item => (
				<ListLayout.Item key={getKey(item.id)}>
					<HubItem
						getCategory={getCategory}
						item={item}
						isMobile={isMobile}
						onNavigate={() => onNavigateToItem(item.id)}
						onActiveFilterChange={onActiveFilterChange}
						onSelectedCategoryIdChange={onSelectedCategoryIdChange}
						trackingId={`event-card|${item.id}`}
						timezone={timezone}
					/>
				</ListLayout.Item>
			))}
		</ListLayout>
	);
};

List.displayName = "List";